import * as appConfig from '../config';
import { resolveKryspaceReadSource } from '../config/kryspaceReadSource';
import { apiClient } from './apiClient';
import {
  isKryspaceControlBridgeStructure,
  type KryspaceControlBridgeStructure,
} from '../types/kryspaceControlBridgeStructure';
import {
  KryspaceRenderServiceError,
  mapRenderManifestHttpError,
} from './kryspaceRenderService';

function readConfig() {
  return {
    apiBaseUrl: appConfig.API_BASE_URL,
    devMock: appConfig.RENDER_SURFACE_DEV_MOCK,
  };
}

function structureUrl(slug: string, source: ReturnType<typeof resolveKryspaceReadSource>): string {
  return `/kryspace/workspace/${encodeURIComponent(slug)}/control-bridge-structure?source=${source}`;
}

function structureSources(slug: string): Array<ReturnType<typeof resolveKryspaceReadSource>> {
  const primary = resolveKryspaceReadSource({ workspaceSlug: slug });
  if (process.env.NODE_ENV !== 'production' && primary === 'db') {
    return ['db', 'workspace'];
  }
  return [primary];
}

function timeoutForStructureSource(source: ReturnType<typeof resolveKryspaceReadSource>): number {
  return source === 'db' ? 90000 : 12000;
}

function buildDevMockStructure(workspaceSlug: string): KryspaceControlBridgeStructure {
  const now = new Date().toISOString();
  const isKfo = workspaceSlug.includes('frontend_ops');

  if (isKfo) {
    return {
      schemaVersion: 'control-bridge-structure/v0.1',
      workspaceSlug,
      generatedAt: now,
      companyTitle: 'Krysalis Frontend Ops Control Bridge',
      rootInstanceId: 'ca-kfo-control-bridge',
      groups: [
        {
          id: 'domains',
          label: 'Domains',
          nodes: [
            {
              key: 'ca-kfo-strategy-domain',
              label: 'Strategy Domain',
              artifactInstanceId: 'ca-kfo-strategy-domain',
              role: 'domain',
              filterScope: 'ca-kfo-strategy-domain',
              children: [],
            },
            {
              key: 'ca-kfo-product-domain',
              label: 'Product Domain',
              artifactInstanceId: 'ca-kfo-product-domain',
              role: 'domain',
              filterScope: 'ca-kfo-product-domain',
              children: [],
            },
            {
              key: 'ca-kfo-resource-domain',
              label: 'Resource Domain',
              artifactInstanceId: 'ca-kfo-resource-domain',
              role: 'domain',
              filterScope: 'ca-kfo-resource-domain',
              children: [],
            },
          ],
        },
        { id: 'domain_bridges', label: 'Domain bridges', nodes: [] },
        {
          id: 'specialty',
          label: 'Specialty',
          nodes: [
            {
              key: 'ca-kfo-bridge-session-domain',
              label: 'Bridge Session Domain',
              role: 'bridge_session',
              filterScope: 'ca-kfo-bridge-session-domain',
            },
          ],
        },
      ],
    };
  }

  return {
    schemaVersion: 'control-bridge-structure/v0.1',
    workspaceSlug,
    generatedAt: now,
    companyTitle: `${workspaceSlug} Control Bridge`,
    groups: [
      { id: 'domains', label: 'Domains', nodes: [] },
      { id: 'domain_bridges', label: 'Domain bridges', nodes: [] },
      { id: 'specialty', label: 'Specialty', nodes: [] },
    ],
  };
}

function mayUseDevMockFallback(err: unknown): boolean {
  const { devMock } = readConfig();
  if (!devMock) return false;
  if (err instanceof KryspaceRenderServiceError) {
    return (
      err.code === 'NOT_CONFIGURED' ||
      err.code === 'NETWORK' ||
      err.code === 'SERVICE_UNAVAILABLE'
    );
  }
  const status = (err as { response?: { status?: number } })?.response?.status;
  if (status === 403 || status === 404 || status === 401) return false;
  return true;
}

export interface FetchControlBridgeStructureResult {
  structure: KryspaceControlBridgeStructure;
  devMock: boolean;
  /** Live API returned 404 (e.g. prime_ops has no company_artifacts sidebar tree). */
  structureUnavailable?: boolean;
}

export async function fetchControlBridgeStructure(
  workspaceSlug: string
): Promise<KryspaceControlBridgeStructure> {
  const result = await fetchControlBridgeStructureWithMeta(workspaceSlug);
  return result.structure;
}

export async function fetchControlBridgeStructureWithMeta(
  workspaceSlug: string
): Promise<FetchControlBridgeStructureResult> {
  const { apiBaseUrl, devMock } = readConfig();
  const slug = workspaceSlug.trim();
  if (!slug) {
    throw new KryspaceRenderServiceError('Workspace slug is required', 'NOT_CONFIGURED');
  }

  if (!apiBaseUrl) {
    if (devMock) {
      return { structure: buildDevMockStructure(slug), devMock: true };
    }
    throw new KryspaceRenderServiceError(
      'Kryspace read API is not configured. Set REACT_APP_API_URL or REACT_APP_RENDER_SURFACE_DEV_MOCK=true.',
      'NOT_CONFIGURED'
    );
  }

  const baseURL = apiBaseUrl.replace(/\/$/, '');
  let lastErr: KryspaceRenderServiceError | undefined;
  const sources = structureSources(slug);

  for (let i = 0; i < sources.length; i += 1) {
    const source = sources[i];
    try {
      const response = await apiClient.get<KryspaceControlBridgeStructure>(
        structureUrl(slug, source),
        {
          baseURL,
          timeout: timeoutForStructureSource(source),
        }
      );
      const data = response.data;
      if (!isKryspaceControlBridgeStructure(data)) {
        throw new KryspaceRenderServiceError(
          'Control Bridge structure response failed schema validation',
          'INVALID_RESPONSE'
        );
      }
      return { structure: data, devMock: false };
    } catch (err: unknown) {
      const serviceErr =
        err instanceof KryspaceRenderServiceError ? err : mapRenderManifestHttpError(err);
      lastErr = serviceErr;

      const hasAnotherSource = i < sources.length - 1;
      if (
        hasAnotherSource &&
        (serviceErr.code === 'NETWORK' ||
          serviceErr.code === 'FORBIDDEN' ||
          serviceErr.code === 'NOT_FOUND' ||
          serviceErr.code === 'SERVICE_UNAVAILABLE')
      ) {
        continue;
      }

      if (serviceErr.code === 'NOT_FOUND') {
        return {
          structure: buildDevMockStructure(slug),
          devMock: false,
          structureUnavailable: true,
        };
      }
      if (
        serviceErr.code === 'FORBIDDEN' ||
        serviceErr.code === 'INVALID_RESPONSE' ||
        serviceErr.code === 'SERVICE_UNAVAILABLE'
      ) {
        throw serviceErr;
      }
      if (mayUseDevMockFallback(serviceErr)) {
        return { structure: buildDevMockStructure(slug), devMock: true };
      }
      throw serviceErr;
    }
  }

  if (lastErr && mayUseDevMockFallback(lastErr)) {
    return { structure: buildDevMockStructure(slug), devMock: true };
  }
  throw lastErr ?? new KryspaceRenderServiceError('Failed to load Control Bridge structure', 'NETWORK');
}
